import { useTranslation } from 'react-i18next';
import { useScheduleContext } from '@/pages/Schedule/contexts';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry, DecoratedInstance } from '@/pages/Schedule/types';
import {
  Menu,
  MenuList,
  MenuItem,
  MenuButton,
  useDisclosure,
  Icon,
} from '@/ui';
import { DeleteEntryConfirmation } from '../../DeleteEntryConfirmation';
import { useEntryModalContext } from '../contexts';
import { HeaderButton } from './HeaderButton';

type Props = {
  entry: DecoratedEntry;
  instance: DecoratedInstance | undefined;
  onDeleteEntry?: () => void;
  onDeleteInstance?: () => void;
};

export const MoreMenu = ({
  entry,
  instance,
  onDeleteEntry,
  onDeleteInstance,
}: Props) => {
  const { t } = useTranslation('entryModal');
  const { startCloneEntryStateTransition } = useEntryModalContext();
  const { scheduleId } = useScheduleContext();

  const { draftEntry, draftMode, cloneDraftEntry } = useDraftEntryStore(
    (state) => ({
      cloneDraftEntry: state.cloneDraftEntry,
      draftMode: state.draftMode,
      draftEntry: state.draftEntry,
    })
  );

  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: openDeleteConfirm,
    onClose: closeDeleteConfirm,
  } = useDisclosure();

  const handleDuplicate = () => {
    if (draftEntry) {
      startCloneEntryStateTransition();
      setTimeout(() => cloneDraftEntry(draftEntry, scheduleId), 500);
    }
  };

  const canDelete = !!onDeleteEntry && !!onDeleteInstance;

  return (
    <>
      {canDelete && (
        <DeleteEntryConfirmation
          entry={entry}
          instance={instance}
          isOpen={isDeleteConfirmOpen}
          mode={draftMode}
          onClose={closeDeleteConfirm}
          onDeleteEntry={onDeleteEntry}
          onDeleteInstance={onDeleteInstance}
        />
      )}

      <Menu isLazy placement="bottom-end">
        <HeaderButton
          as={MenuButton}
          label="More"
          leftIcon={<Icon icon="MoreHorizontal" size="5" />}
        />
        <MenuList fontSize="md">
          <MenuItem
            icon={<Icon icon="Copy" />}
            isDisabled={draftMode !== 'edit'}
            onClick={handleDuplicate}
          >
            {t('duplicate.label')}
          </MenuItem>
          {canDelete && (
            <MenuItem
              icon={<Icon icon="Trash" />}
              onClick={() => openDeleteConfirm()}
            >
              {t('delete')}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};
